import { Button, Text } from '@components/ui';
import { BuyerJobListSkeleton } from '@features/buyer/jobs/buyer-job-list-skeleton';
import { JobKindChip, type JobKindSource } from '@features/jobs';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export type BuyerListJob = JobKindSource & {
  id: string | number;
  askingPrice?: string | number;
  status?: string;
  modle?: string | null;
  vinOrSerial?: string | null;
  city?: string | null;
  state?: string | null;
  address?: string | null;
  contactName?: string | null;
  category?: JobKindSource['category'] & { icon?: string | null };
};

export const buyerJobTitle = (job: BuyerListJob, fallback: string): string => {
  const vehicle = [job.year, job.make, job.model || job.modle]
    .filter(Boolean)
    .join(' ');
  if (vehicle) return vehicle;
  return (
    job.description || job.subCategory?.name || job.category?.name || fallback
  );
};

const uniqueParts = (parts: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  return parts.filter((part): part is string => {
    if (!part) return false;
    const key = part.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const statusColor = (status: string): string => {
  switch (status) {
    case 'Accepted':
    case 'Media Pending':
      return 'success.main';
    default:
      return 'text.secondary';
  }
};

const EmptyState = ({
  icon,
  title,
  hint,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
}): JSX.Element => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1,
      px: 3,
      py: 6,
      border: 1,
      borderColor: 'divider',
      borderRadius: 3,
      bgcolor: 'background.paper',
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 56,
        height: 56,
        borderRadius: '50%',
        bgcolor: 'action.hover',
        color: 'text.secondary',
        mb: 0.5,
      }}
    >
      {icon}
    </Box>
    <Text variant="subtitle" sx={{ fontSize: 15, fontWeight: 700 }}>
      {title}
    </Text>
    {hint ? (
      <Text
        variant="body"
        tone="muted"
        sx={{ maxWidth: 320, fontSize: 13, lineHeight: 1.5 }}
      >
        {hint}
      </Text>
    ) : null}
  </Box>
);

interface BuyerJobListProps {
  jobs: BuyerListJob[];
  loading?: boolean;
  searching?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onOpen: (job: BuyerListJob) => void;
}

export const BuyerJobList = ({
  jobs,
  loading = false,
  searching = false,
  emptyTitle,
  emptyHint,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  onOpen,
}: BuyerJobListProps): JSX.Element => {
  const { t } = useTranslation();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || !onLoadMore) return;
    const node = loadMoreRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((entry) => entry.isIntersecting) &&
          !isFetchingNextPage
        ) {
          onLoadMore();
        }
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  if (loading) {
    return <BuyerJobListSkeleton />;
  }

  if (jobs.length === 0) {
    return (
      <EmptyState
        icon={
          searching ? (
            <SearchOffRoundedIcon sx={{ fontSize: 28 }} />
          ) : (
            <WorkOutlineRoundedIcon sx={{ fontSize: 28 }} />
          )
        }
        title={emptyTitle ?? t('jobs.empty')}
        hint={emptyHint}
      />
    );
  }

  return (
    <Box
      sx={{
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
      }}
    >
      {jobs.map((job) => {
        const title = buyerJobTitle(job, t('jobDetail.fallbackTitle'));
        const subtitle = uniqueParts([
          job.category?.name,
          job.subCategory?.name,
        ])
          .filter((part) => !title.toLowerCase().includes(part.toLowerCase()))
          .join(' · ');
        const price =
          job.askingPrice == null
            ? t('jobs.priceUnavailable')
            : `$${Number(job.askingPrice).toLocaleString('en-US')}`;

        return (
          <Box
            key={job.id}
            component="button"
            type="button"
            onClick={() => onOpen(job)}
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.75,
              border: 0,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
              ...(hasNextPage ? {} : { '&:last-of-type': { borderBottom: 0 } }),
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'flex',
                height: 44,
                width: 44,
                flexShrink: 0,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            >
              {job.category?.icon ? (
                <Box
                  component="img"
                  src={job.category.icon}
                  alt=""
                  sx={{ height: 28, width: 28, objectFit: 'contain' }}
                />
              ) : null}
            </Box>
            <Box component="span" sx={{ minWidth: 0, flex: 1 }}>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                }}
              >
                {title}
              </Box>
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.5,
                }}
              >
                {subtitle ? (
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 13,
                      color: 'text.secondary',
                    }}
                  >
                    {subtitle}
                  </Box>
                ) : null}
                <JobKindChip job={job} />
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                display: 'flex',
                flexShrink: 0,
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0.25,
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {price}
              </Box>
              {job.status ? (
                <Text
                  as="span"
                  variant="caption"
                  sx={{ color: statusColor(job.status) }}
                >
                  {job.status}
                </Text>
              ) : null}
            </Box>
          </Box>
        );
      })}
      {hasNextPage ? (
        <Box
          ref={loadMoreRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            variant="secondary"
            loading={isFetchingNextPage}
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {t('jobs.loadMore')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
